/**
 * Contrato da frota: veículos, manutenções e a agenda de oficinas.
 *
 * Papel: aqui ficam os schemas dos três cadastros do módulo —
 * `novoVeiculoSchema`, `novaManutencaoVeiculoSchema` e `novaOficinaSchema` na
 * entrada, e os três correspondentes na saída.
 *
 * Como usar: a manutenção aponta para o veículo por `veiculoId`, e nunca por
 * placa ou modelo. Quem precisa dos dois os recebe aninhados em
 * `manutencaoVeiculoSchema`, sempre iguais aos do veículo apontado — é o que
 * impede a placa mostrada no histórico de divergir da que está no cadastro.
 *
 * `classeBonus` e `foraDaApolice` andam juntos: o veículo que não está na
 * apólice não tem classe de bônus, e o schema recusa a combinação em que os
 * dois vêm preenchidos. Sem a recusa, um número de classe sobreviveria ao
 * veículo sair do seguro e apareceria na tela como se ainda valesse.
 */
import { z } from 'zod';

import {
  dataOpcionalSchema,
  dataSchema,
  emailOpcionalSchema,
  idSchema,
  telefoneOpcionalSchema,
  textoObrigatorio,
  textoOpcional,
} from './comum';
import {
  chassiOpcionalSchema,
  dinheiroOpcionalSchema,
  fipeOpcionalSchema,
  placaSchema,
  renavamOpcionalSchema,
} from './formatos';

/** O primeiro ano que um veículo da frota pode ter. */
const ANO_MINIMO = 1900;

/** O último ano aceito, com folga para o modelo do ano seguinte. */
const ANO_MAXIMO = 2200;

/** A maior classe de bônus que uma seguradora concede. */
const CLASSE_BONUS_MAXIMA = 10;

/**
 * Número inteiro opcional que normaliza vazio para `null`.
 *
 * O formulário entrega string vazia no campo numérico não preenchido, e
 * `z.coerce.number()` a converteria em zero — que é um valor legítimo em
 * `classeBonus` e em `km`, e por isso não pode ser o que a ausência produz.
 *
 * @param minimo - O menor valor aceito.
 * @param maximo - O maior valor aceito.
 * @returns Schema que devolve o inteiro ou `null`.
 */
function inteiroOpcional(minimo: number, maximo: number) {
  return z
    .union([z.number(), z.string()])
    .nullish()
    .transform((valor) => {
      if (valor == null) return null;
      const texto = typeof valor === 'number' ? String(valor) : valor.trim();
      return texto === '' ? null : Number(texto);
    })
    .refine(
      (valor) =>
        valor === null || (Number.isInteger(valor) && valor >= minimo && valor <= maximo),
      { message: `Informe um número inteiro entre ${String(minimo)} e ${String(maximo)}.` },
    );
}

/**
 * O que o formulário envia para criar ou alterar um veículo.
 *
 * `codigo` é o nome curto pelo qual a frota é chamada no dia a dia — `V01`,
 * `V02` — e é único, mas não é a chave: a chave é o `id` do banco. Renumerar a
 * frota passa a ser uma alteração, e não a quebra de todo vínculo.
 *
 * `categoria` é texto, e não escolha entre valores fixos: os valores em uso são
 * o que a frota tem hoje, e van, hatch e utilitário cabem no mesmo campo sem
 * que isso precise de uma migration.
 */
export const novoVeiculoSchema = z
  .object({
    codigo: textoObrigatorio('Código', 20),
    marca: textoObrigatorio('Marca', 60),
    modelo: textoObrigatorio('Modelo', 160),
    ano: inteiroOpcional(ANO_MINIMO, ANO_MAXIMO),
    placa: placaSchema,
    chassi: chassiOpcionalSchema,
    renavam: renavamOpcionalSchema,
    categoria: textoObrigatorio('Categoria', 60),
    lotacao: inteiroOpcional(1, 99),
    fipe: fipeOpcionalSchema,
    classeBonus: inteiroOpcional(0, CLASSE_BONUS_MAXIMA),
    foraDaApolice: z.boolean().default(false),
    blindado: z.boolean(),
    observacao: textoOpcional(2000),
  })
  .refine((veiculo) => !veiculo.foraDaApolice || veiculo.classeBonus === null, {
    message: 'O veículo fora da apólice não tem classe de bônus.',
    path: ['classeBonus'],
  });

export type NovoVeiculo = z.infer<typeof novoVeiculoSchema>;

/** Um veículo como a API o devolve. */
export const veiculoSchema = z.object({
  id: idSchema,
  codigo: z.string(),
  marca: z.string(),
  modelo: z.string(),
  ano: z.number().nullable(),
  placa: z.string(),
  chassi: z.string().nullable(),
  renavam: z.string().nullable(),
  categoria: z.string(),
  lotacao: z.number().nullable(),
  fipe: z.string().nullable(),
  classeBonus: z.number().nullable(),
  foraDaApolice: z.boolean(),
  blindado: z.boolean(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Veiculo = z.infer<typeof veiculoSchema>;

/** O veículo da manutenção, como a listagem o traz aninhado. */
export const veiculoDaManutencaoSchema = z.object({
  id: idSchema,
  codigo: z.string(),
  placa: z.string(),
  marca: z.string(),
  modelo: z.string(),
});

/**
 * O que o formulário envia para criar ou alterar uma manutenção.
 *
 * `data` é obrigatória, ao contrário de quase toda data do projeto: uma
 * manutenção sem data não entra em nenhuma ordenação nem em nenhum total por
 * período, que é para o que o histórico serve.
 *
 * `proxData` e `proxKm` são o que transforma o histórico em agenda — quando é a
 * próxima revisão e com qual quilometragem ela vence. A quilometragem seguinte
 * não pode ser menor que a da própria manutenção; é erro de digitação, e
 * passaria a anunciar uma revisão vencida no dia em que fosse cadastrada.
 */
export const novaManutencaoVeiculoSchema = z
  .object({
    veiculoId: idSchema,
    data: dataSchema,
    km: inteiroOpcional(0, 9_999_999),
    tipo: textoObrigatorio('Tipo de serviço', 80),
    descricao: textoOpcional(500),
    oficina: textoOpcional(200),
    valor: dinheiroOpcionalSchema,
    proxData: dataOpcionalSchema,
    proxKm: inteiroOpcional(0, 9_999_999),
  })
  .refine(
    (manutencao) =>
      manutencao.km === null ||
      manutencao.proxKm === null ||
      manutencao.proxKm >= manutencao.km,
    {
      message: 'A quilometragem da próxima manutenção não pode ser menor que a atual.',
      path: ['proxKm'],
    },
  )
  .refine(
    (manutencao) => manutencao.proxData === null || manutencao.proxData >= manutencao.data,
    { message: 'A próxima manutenção não pode ser anterior a esta.', path: ['proxData'] },
  );

export type NovaManutencaoVeiculo = z.infer<typeof novaManutencaoVeiculoSchema>;

/**
 * Uma manutenção como a API a devolve.
 *
 * O veículo vem aninhado, e não apenas como `veiculoId`, porque a tabela mostra
 * de qual carro é a manutenção em toda linha. Placa e modelo saem do cadastro
 * do veículo, e não de cópias na própria manutenção — que era o que fazia os
 * dois divergirem.
 */
export const manutencaoVeiculoSchema = z.object({
  id: idSchema,
  veiculo: veiculoDaManutencaoSchema,
  data: z.string(),
  km: z.number().nullable(),
  tipo: z.string(),
  descricao: z.string().nullable(),
  oficina: z.string().nullable(),
  valor: z.number().nullable(),
  proxData: z.string().nullable(),
  proxKm: z.number().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type ManutencaoVeiculo = z.infer<typeof manutencaoVeiculoSchema>;

/**
 * O que o formulário envia para criar ou alterar uma oficina da agenda.
 *
 * Só `nome` é obrigatório, como na agenda do Mailing e na de médicos: uma
 * oficina de quem se tem apenas o nome e o telefone precisa poder ser cadastrada
 * sem que se invente o resto.
 */
export const novaOficinaSchema = z.object({
  nome: textoObrigatorio('Nome'),
  servico: textoOpcional(120),
  telefone: telefoneOpcionalSchema,
  email: emailOpcionalSchema,
  endereco: textoOpcional(300),
  observacao: textoOpcional(2000),
});

export type NovaOficina = z.infer<typeof novaOficinaSchema>;

/** Uma oficina da agenda como a API a devolve. */
export const oficinaSchema = z.object({
  id: idSchema,
  nome: z.string(),
  servico: z.string().nullable(),
  telefone: z.string().nullable(),
  email: z.string().nullable(),
  endereco: z.string().nullable(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Oficina = z.infer<typeof oficinaSchema>;
